import { useState } from 'react';
import { trpc } from '@/trpc/client';

type Role = 'admin' | 'editor' | 'viewer';
type AdminUserStatus = 'invited' | 'active' | 'disabled';

interface AdminUserRow {
  email: string;
  role: Role;
  status: AdminUserStatus;
  cognitoSub: string | null;
  displayName: string | null;
  invitedBy: string | null;
  invitedAt: string;
  firstLoginAt: string | null;
  lastLoginAt: string | null;
}

const ROLES: Role[] = ['admin', 'editor', 'viewer'];

/**
 * Admin-only user management screen. Lists every {@link AdminUser},
 * lets an admin invite a new operator (email + role), and per row
 * change the role, disable, or re-enable the account. Mutations
 * invalidate `trpc.user.list` so the table reflects the new state.
 */
export function Users(): JSX.Element {
  const utils = trpc.useUtils();
  const query = trpc.user.list.useQuery();

  const invalidate = () => {
    void utils.user.list.invalidate();
  };

  const invite = trpc.user.invite.useMutation({ onSuccess: invalidate });
  const updateRole = trpc.user.updateRole.useMutation({ onSuccess: invalidate });
  const disable = trpc.user.disable.useMutation({ onSuccess: invalidate });
  const enable = trpc.user.enable.useMutation({ onSuccess: invalidate });

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('viewer');

  const handleInvite = (event: React.FormEvent) => {
    event.preventDefault();
    if (!email) return;
    invite.mutate({ email, role });
    setEmail('');
    setRole('viewer');
  };

  const users = (query.data?.users as AdminUserRow[] | undefined) ?? [];

  return (
    <div>
      <h1 className="text-2xl font-bold">管理者ユーザー</h1>

      <form onSubmit={handleInvite} className="mt-4 flex flex-wrap items-end gap-2 text-sm">
        <label className="flex flex-col">
          <span className="text-xs text-gray-600">メール</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-label="invite-email"
            placeholder="user@example.com"
            className="rounded border border-gray-300 px-2 py-1"
          />
        </label>
        <label className="flex flex-col">
          <span className="text-xs text-gray-600">ロール</span>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            aria-label="invite-role"
            className="rounded border border-gray-300 px-2 py-1"
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </label>
        <button
          type="submit"
          disabled={invite.isPending || !email}
          className="rounded bg-slate-800 px-3 py-1 text-white disabled:opacity-50"
        >
          招待
        </button>
        {invite.error ? (
          <span className="text-xs text-red-600">エラー: {invite.error.message}</span>
        ) : null}
      </form>

      <div className="mt-6">
        {query.isLoading ? (
          <p className="text-sm text-gray-500">読み込み中…</p>
        ) : query.error ? (
          <p className="text-sm text-red-600">エラー: {query.error.message}</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-100 text-left">
                <th className="p-2">メール</th>
                <th className="p-2">ロール</th>
                <th className="p-2">状態</th>
                <th className="p-2">最終ログイン</th>
                <th className="p-2">操作</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.email} className="border-b">
                  <td className="p-2">{u.email}</td>
                  <td className="p-2">
                    <select
                      value={u.role}
                      onChange={(e) =>
                        updateRole.mutate({ email: u.email, role: e.target.value as Role })
                      }
                      aria-label={`role-${u.email}`}
                      disabled={updateRole.isPending}
                      className="rounded border border-gray-300 px-1 py-0.5"
                    >
                      {ROLES.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="p-2">{u.status}</td>
                  <td className="p-2 font-mono text-xs">{u.lastLoginAt ?? '-'}</td>
                  <td className="p-2">
                    {u.status === 'disabled' ? (
                      <button
                        type="button"
                        onClick={() => enable.mutate({ email: u.email })}
                        disabled={enable.isPending}
                        className="rounded border border-gray-300 px-2 py-0.5 text-xs hover:bg-gray-50 disabled:opacity-50"
                      >
                        有効化
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => disable.mutate({ email: u.email })}
                        disabled={disable.isPending}
                        className="rounded border border-red-300 px-2 py-0.5 text-xs text-red-700 hover:bg-red-50 disabled:opacity-50"
                      >
                        無効化
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
